import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { LocationNode, MeasurementSeries } from '@oscom/shared';
import { getCustomer } from '../api/customers';
import { listLocations } from '../api/locations';
import { deleteMeasurementSeries, listMeasurementSeries } from '../api/measurements';
import { useSession } from '../auth/SessionProvider';
import { CustomerContextNav } from '../components/CustomerContextNav';
import { Layout } from '../components/Layout';
import { MeasurementImportDialog } from '../components/MeasurementImportDialog';
import { NotFoundPage } from './NotFoundPage';

/** `2026-01-15` -> `15.01.2026`; anything else is shown unchanged. */
function formatDate(isoDate: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : isoDate;
}

/**
 * Measurement series of one customer, with the import dialog.
 *
 * Reading needs `measurements:read`, importing and deleting
 * `measurements:write`. The page only exists while the `measurements` feature
 * switch is on; `features` is read defensively (deploy skew -> "not found").
 */
export function MeasurementSeriesPage(): ReactNode {
  const { session } = useSession();
  const navigate = useNavigate();
  const { customerId: customerIdParam } = useParams<{ customerId: string }>();
  const enabled = session?.status === 'authenticated' && session.me.features?.measurements === true;

  const [customerName, setCustomerName] = useState<string | undefined>(undefined);
  const [locations, setLocations] = useState<LocationNode[]>([]);
  const [seriesList, setSeriesList] = useState<MeasurementSeries[] | undefined>(undefined);
  const [loadError, setLoadError] = useState<string | undefined>(undefined);
  const [importing, setImporting] = useState(false);
  const [confirmingId, setConfirmingId] = useState<string | undefined>(undefined);
  const [deleting, setDeleting] = useState(false);
  const [actionError, setActionError] = useState<string | undefined>(undefined);

  const loadAll = useCallback((id: string, cancelledRef: { current: boolean }) => {
    Promise.all([getCustomer(id), listLocations(id), listMeasurementSeries(id)])
      .then(([customer, locationResponse, seriesResponse]) => {
        if (cancelledRef.current) return;
        setCustomerName(customer.name);
        setLocations(locationResponse.locations);
        setSeriesList(seriesResponse.series);
        setLoadError(undefined);
      })
      .catch((caught: unknown) => {
        if (cancelledRef.current) return;
        setLoadError(
          caught instanceof Error ? caught.message : 'Messreihen konnten nicht geladen werden.',
        );
      });
  }, []);

  useEffect(() => {
    if (!customerIdParam || !enabled) return;
    const cancelledRef = { current: false };
    loadAll(customerIdParam, cancelledRef);
    return () => {
      cancelledRef.current = true;
    };
  }, [customerIdParam, enabled, loadAll]);

  if (session?.status !== 'authenticated') return null;
  if (!enabled) return <NotFoundPage />;
  if (!customerIdParam) return null;
  const { me } = session;
  const customerId = customerIdParam;
  const canWrite = me.permissions.includes('measurements:write');
  const base = `/kunden/${encodeURIComponent(customerId)}/messungen`;

  async function handleDelete(seriesId: string): Promise<void> {
    setActionError(undefined);
    setDeleting(true);
    try {
      await deleteMeasurementSeries(customerId, seriesId);
      setConfirmingId(undefined);
      loadAll(customerId, { current: false });
    } catch (caught: unknown) {
      setConfirmingId(undefined);
      setActionError(caught instanceof Error ? caught.message : 'Löschen fehlgeschlagen.');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Layout user={{ displayName: me.displayName, role: me.role }} showLogout>
      <section className="stack">
        {customerName ? (
          <CustomerContextNav
            customerId={customerId}
            customerName={customerName}
            features={me.features}
          />
        ) : null}

        <div className="card">
          <h1>Messungen</h1>
          {canWrite && !importing ? (
            <p>
              <button
                type="button"
                className="button button--primary"
                onClick={() => setImporting(true)}
              >
                Messdateien importieren
              </button>
            </p>
          ) : null}

          {actionError ? <p className="notice notice--warning">{actionError}</p> : null}

          {loadError ? (
            <p className="notice notice--warning">{loadError}</p>
          ) : seriesList === undefined ? (
            <p className="muted" role="status" aria-live="polite">
              Messreihen werden geladen ...
            </p>
          ) : seriesList.length === 0 ? (
            <p className="muted">Noch keine Messreihe importiert.</p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Bezeichnung</th>
                    <th scope="col">Datum</th>
                    <th scope="col">Punkte</th>
                    <th scope="col">Status</th>
                    {canWrite ? <th scope="col">Aktion</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {seriesList.map((entry) => (
                    <tr key={entry.id}>
                      <td>
                        <Link to={`${base}/${encodeURIComponent(entry.id)}`}>{entry.label}</Link>
                      </td>
                      <td>{formatDate(entry.measuredOn)}</td>
                      <td>{entry.pointCount}</td>
                      <td>
                        {entry.status === 'importing'
                          ? 'Import unvollständig – löschen und neu importieren'
                          : 'Vollständig'}
                      </td>
                      {canWrite ? (
                        <td>
                          {confirmingId === entry.id ? (
                            <span role="alertdialog" aria-live="polite">
                              Messreihe „{entry.label}“ löschen? Die Rohdateien bleiben archiviert.{' '}
                              <button
                                type="button"
                                className="button button--primary"
                                disabled={deleting}
                                onClick={() => void handleDelete(entry.id)}
                              >
                                {deleting ? 'Löscht ...' : 'Löschen'}
                              </button>{' '}
                              <button
                                type="button"
                                className="button button--ghost"
                                disabled={deleting}
                                onClick={() => setConfirmingId(undefined)}
                              >
                                Abbrechen
                              </button>
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="button button--ghost"
                              onClick={() => {
                                setActionError(undefined);
                                setConfirmingId(entry.id);
                              }}
                            >
                              Löschen
                            </button>
                          )}
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {canWrite && importing ? (
          <MeasurementImportDialog
            customerId={customerId}
            locations={locations}
            onClose={() => setImporting(false)}
            onImported={(series) => {
              setImporting(false);
              navigate(`${base}/${encodeURIComponent(series.id)}`);
            }}
          />
        ) : null}
      </section>
    </Layout>
  );
}
